import { CellNode, CheckboxNode, DateNode, DropdownElementNode, DropdownGroupNode, DynamoNode, ElementNode, ElementType, FormNode, InputNode, InvalidNode, LabelNode, NodeTag, PageNode, RadioButtonElementNode, RadioButtonGroupNode, RowNode, SectionNode, TableNode, TextBoxNode, ImageNode, IDimensions, IBorderRadius, IPadding, createEmptyStyle, IBorderWidth } from "dynamo-models";
import { DynComponent, NodeProps } from "..";

export abstract class AbsComponentFactory {
    public abstract Prepare(node: FormNode);

    protected abstract CreateFormComponent(children: [], nodeProps: NodeProps<FormNode>): JSX.Element;

    protected abstract CreatePageComponent(children: [], nodeProps: NodeProps<PageNode>): JSX.Element;

    protected abstract CreateCellComponent(children: [], nodeProps: NodeProps<CellNode>): JSX.Element;

    protected abstract CreateRowComponent(children: [], nodeProps: NodeProps<RowNode>): JSX.Element;

    protected abstract CreateTableComponent(children: [], nodeProps: NodeProps<TableNode>): JSX.Element;

    protected abstract CreateDropdownGroupComponent(children: [], nodeProps: NodeProps<DropdownGroupNode>): JSX.Element;

    protected abstract CreateDropdownElementComponent(children: [], nodeProps: NodeProps<DropdownElementNode>): JSX.Element;

    protected abstract CreateRadioButtonGroupComponent(children: [], nodeProps: NodeProps<RadioButtonGroupNode>): JSX.Element;

    protected abstract CreateRadioButtonElementComponent(children: [], nodeProps: NodeProps<RadioButtonElementNode>): JSX.Element;

    protected abstract CreateSectionComponent(children: [], nodeProps: NodeProps<SectionNode>): JSX.Element;

    protected abstract CreateLabelComponent(children: [], nodeProps: NodeProps<LabelNode>): JSX.Element;

    protected abstract CreateCheckboxComponent(children: [], nodeProps: NodeProps<CheckboxNode>): JSX.Element;

    protected abstract CreateTextBoxComponent(children: [], nodeProps: NodeProps<TextBoxNode>): JSX.Element;

    protected abstract CreateDateComponent(children: [], nodeProps: NodeProps<DateNode>): JSX.Element;

    protected abstract CreateImageComponent(children: [], nodeProps: NodeProps<ImageNode>): JSX.Element;

    public CreateComponent(children: [], nodeProps: NodeProps<FormNode>): JSX.Element;

    public CreateComponent(children: [],
                           nodeProps:
                            NodeProps<PageNode> | NodeProps<SectionNode> | NodeProps<TableNode> | NodeProps<RowNode> | NodeProps<CellNode> |
                            NodeProps<DropdownGroupNode> | NodeProps<DropdownElementNode> | NodeProps<RadioButtonGroupNode> | NodeProps<RadioButtonElementNode> |
                            NodeProps<LabelNode> | NodeProps<CheckboxNode> | NodeProps<TextBoxNode> | NodeProps<DateNode> | NodeProps<InvalidNode> |
                            NodeProps<ImageNode>,
        ): JSX.Element;

    public CreateComponent(children: DynComponent<DynamoNode>[], nodeProps: NodeProps<DynamoNode>): JSX.Element | null {
        switch (nodeProps.node.tag) {
            case NodeTag.Form:
            return this.CreateFormComponent(children as [], nodeProps as NodeProps<FormNode>);

            case NodeTag.Page:
            return this.CreatePageComponent(children as [], nodeProps as NodeProps<PageNode>);

            case NodeTag.Element:
            return this.CreateElementComponent(children as [], nodeProps as NodeProps<ElementNode>);

            default:
            return null
        }
    }

    protected GetCommonProps(node: ElementNode) {
        return {
            href: node.url,
            src: node.url,
            maxLength: node.maxLength,
            style: {
                opacity: node.isHidden ? 0 : 1,
            },
            text: node.text,
            className: node.style.className,
        };
    }

    protected GetCommonInputProps(node: InputNode<any>): any {
        return {
            ...this.GetCommonProps(node),
            placeholder: node.placeholder,
            disabled: node.isDisabled,
            required: node.isRequired(),
            error: node.hasErrors,
        };
    }

    protected getDimensionStyle(node: DynamoNode, defaultDim: IDimensions) {
        const style = ({
            width: this.toEm(defaultDim.width),
            height: this.toEm(defaultDim.height),
        });

        const dim = node.style.dimensions;
        if (dim.width !== undefined) {
            style.width = this.toEm(dim.width);
        }

        if (dim.height !== undefined) {
            style.height = this.toEm(dim.height);
        }

        return style;
    }

    protected getBorderStyle(node: DynamoNode) {
        const {
            radii,
            width,
            color,
        } = node.style.border;

        const borderStyle = width.left !== undefined || width.top !== undefined || width.right !== undefined || width.bottom !== undefined ? "solid" : undefined;

        const style: React.CSSProperties = ({
            borderTopLeftRadius: this.toEm(radii.topLeft, 0),
            borderTopRightRadius: this.toEm(radii.topRight, 0),
            borderBottomRightRadius: this.toEm(radii.bottomRight, 0),
            borderBottomLeftRadius: this.toEm(radii.bottomLeft, 0),

            borderLeftWidth: this.toEm(width.left, 0),
            borderTopWidth: this.toEm(width.top, 0),
            borderRightWidth: this.toEm(width.right, 0),
            borderBottomWidth: this.toEm(width.bottom, 0),

            borderColor: color !== undefined ? color.toHex() : undefined,
            borderStyle,
        });

        return style;
    }

    protected getPaddingStyle(node: DynamoNode) {
        const {
            padding,
        } = node.style;
        const style = ({
            paddingLeft: this.toEm(padding.left),
            paddingTop: this.toEm(padding.top),
            paddingRight: this.toEm(padding.right),
            paddingBottom: this.toEm(padding.bottom),
        });

        return style;
    }

    protected getNodeStyle(node: DynamoNode, defaultDimensions?: IDimensions): React.CSSProperties {
        const {
            backgroundColor,
            horizontalAlignment,
            className,
        } = node.style;
        const emptyStyle = createEmptyStyle();
        const defDimSurely: IDimensions = defaultDimensions !== undefined ? defaultDimensions : emptyStyle.dimensions;

        if (className) {
            return {};
        }

        let justifyContent: string;
        switch (horizontalAlignment) {
            case "right":
            justifyContent = "flex-end";
            break;

            case "center":
            justifyContent = "center";
            break;

            case "left":
            default:
            justifyContent = "flex-start";
            break;
        }

        const style: React.CSSProperties = ({
            ...this.getDimensionStyle(node, defDimSurely),
            ...this.getBorderStyle(node),
            ...this.getPaddingStyle(node),
            display: "flex",
            backgroundColor: backgroundColor !== undefined ? backgroundColor.toHex() : undefined,
            justifyContent,
        });

        if (node.tag === "Element" && (node as ElementNode).elementType === ElementType.VariableText) {
            style.display = "block";
        }

        return style;
    }

    private toEm(value: number | undefined, defaultValue?: number) {
        if (value === undefined && defaultValue === undefined) {
            return undefined;
        }
        else if (value === undefined) {
            return `${defaultValue}em`;        
        }

        return `${value}em`;
    }

    private CreateElementComponent(children: [], nodeProps: NodeProps<ElementNode>): JSX.Element | null {
        switch (nodeProps.node.elementType) {
            case ElementType.Cell:
            return this.CreateCellComponent(children, nodeProps as NodeProps<CellNode>);

            case ElementType.Checkbox:
            return this.CreateCheckboxComponent(children, nodeProps as NodeProps<CheckboxNode>);

            case ElementType.Date:
            return this.CreateDateComponent(children, nodeProps as NodeProps<DateNode>);

            case ElementType.DropdownElement:
            return this.CreateDropdownElementComponent(children, nodeProps as NodeProps<DropdownElementNode>);

            case ElementType.DropdownGroup:
            return this.CreateDropdownGroupComponent(children, nodeProps as NodeProps<DropdownGroupNode>);

            case ElementType.RadioButtonElement:
            return this.CreateRadioButtonElementComponent(children, nodeProps as NodeProps<RadioButtonElementNode>);

            case ElementType.RadioButtonGroup:
            return this.CreateRadioButtonGroupComponent(children, nodeProps as NodeProps<RadioButtonGroupNode>);

            case ElementType.Row:
            return this.CreateRowComponent(children, nodeProps as NodeProps<RowNode>);

            case ElementType.Section:
            return this.CreateSectionComponent(children, nodeProps as NodeProps<SectionNode>);

            case ElementType.Table:
            return this.CreateTableComponent(children, nodeProps as NodeProps<TableNode>);

            case ElementType.VariableText:
            return this.CreateLabelComponent(children, nodeProps as NodeProps<LabelNode>);

            case ElementType.Textbox:
            return this.CreateTextBoxComponent(children, nodeProps as NodeProps<TextBoxNode>);

            case ElementType.Image:
            return this.CreateImageComponent(children, nodeProps as NodeProps<ImageNode>);

            default:
            return null;
        }
    }
}
